import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrandGuidePublicPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/branding/public/${token}`)
      .then((r) => setD(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Guide konnte nicht geladen werden"));
  }, [token]);

  if (error) return <div className="min-h-screen bg-white flex items-center justify-center px-6"><p className="text-sm text-slate-600" data-testid="public-guide-error">{error}</p></div>;
  if (!d) return <div className="min-h-screen bg-white flex items-center justify-center"><p className="text-sm text-slate-400">Lädt…</p></div>;

  const ai = d.ai || {};
  const a = d.answers || {};
  const colors = (d.colors || []).filter(Boolean);
  const accent = colors[0] || "#111827";
  const images = (d.assets || []).filter((x) => x.kind === "image");
  const profiles = (d.assets || []).filter((x) => x.kind === "profile");
  const logo = (d.assets || []).find((x) => x.kind === "logo");
  const asset = (id) => `${API}/branding/public/${token}/asset/${id}`;
  const list = (v) => (Array.isArray(v) ? v : String(v || "").split(",").map((x) => x.trim()).filter(Boolean));

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="public-guide">
      {/* Hero */}
      <header className="px-6 sm:px-12 py-20 sm:py-28" style={{ background: "#0F172A" }}>
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-400">Brand Guide</div>
            <h1 className="font-heading text-5xl sm:text-7xl font-light tracking-tighter text-white mt-4">{d.name}</h1>
            {ai.tagline && <p className="text-lg text-slate-300 italic mt-4">„{ai.tagline}“</p>}
            {logo && <img src={asset(logo.asset_id)} alt="Logo" className="h-16 mt-10 object-contain" />}
          </motion.div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 sm:px-12 py-16 space-y-20">
        {(ai.vision || a.goal) && (
          <Section title="Vision" accent={accent}>
            <p className="text-xl font-light leading-relaxed text-slate-800">{ai.vision || a.goal}</p>
            {ai.positioning && <p className="text-sm text-slate-600 mt-6">{ai.positioning}</p>}
          </Section>
        )}

        {list(ai.mission).length > 0 && (
          <Section title="Mission" accent={accent}>
            <ul className="space-y-3">
              {list(ai.mission).map((m, i) => (
                <li key={i} className="flex gap-3 text-base text-slate-700">
                  <span className="mt-2 h-1.5 w-1.5 flex-shrink-0" style={{ background: accent }} />{m}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {(list(a.adjectives).length > 0 || ai.personality) && (
          <Section title="Markenpersönlichkeit" accent={accent}>
            <div className="flex flex-wrap gap-2 mb-6">
              {list(a.adjectives).map((w) => (
                <span key={w} className="px-3 py-1.5 bg-slate-100 text-sm">{w}</span>
              ))}
            </div>
            <p className="text-base text-slate-700 leading-relaxed">{ai.personality}</p>
          </Section>
        )}

        {colors.length > 0 && (
          <Section title={`Farben${d.palette ? ` · ${d.palette.name}` : ""}`} accent={accent}>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {colors.map((c) => (
                <div key={c}>
                  <div className="h-28 border border-slate-200" style={{ background: c }} />
                  <div className="text-xs font-mono mt-2 text-slate-700">{c.toUpperCase()}</div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {ai.typography && (
          <Section title="Typografie" accent={accent}>
            <div className="text-4xl font-light tracking-tight">{ai.typography.heading}</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] mt-1" style={{ color: accent }}>Überschriften</div>
            <div className="text-2xl font-light mt-8">{ai.typography.body}</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] mt-1" style={{ color: accent }}>Fließtext</div>
            {ai.typography.why && <p className="text-sm text-slate-600 mt-6">{ai.typography.why}</p>}
          </Section>
        )}

        {images.length > 0 && (
          <Section title="Bildsprache" accent={accent}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {images.map((x) => <img key={x.asset_id} src={asset(x.asset_id)} alt="" loading="lazy" className="w-full h-52 object-cover" />)}
            </div>
            {ai.imagery && <p className="text-sm text-slate-600 mt-6">{ai.imagery}</p>}
          </Section>
        )}

        {profiles.length > 0 && (
          <Section title="Fotos" accent={accent}>
            <div className="flex gap-4 flex-wrap">
              {profiles.map((x) => <img key={x.asset_id} src={asset(x.asset_id)} alt="" loading="lazy" className="w-40 h-52 object-cover" />)}
            </div>
          </Section>
        )}

        {(list(ai.voice_attributes).length > 0 || ai.voice_description) && (
          <Section title="Tone of Voice" accent={accent}>
            <div className="flex flex-wrap gap-2 mb-6">
              {list(ai.voice_attributes).map((w) => (
                <span key={w} className="px-4 py-2 text-sm text-white" style={{ background: accent }}>{w}</span>
              ))}
            </div>
            <p className="text-base text-slate-700 leading-relaxed">{ai.voice_description}</p>
          </Section>
        )}

        {(list(ai.dos).length > 0 || list(ai.donts).length > 0) && (
          <Section title="Do's & Don'ts" accent={accent}>
            <div className="grid sm:grid-cols-2 gap-10">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700 mb-3">Do's</div>
                <ul className="space-y-2 text-sm text-slate-700">{list(ai.dos).map((x, i) => <li key={i}>· {x}</li>)}</ul>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-red-700 mb-3">Don'ts</div>
                <ul className="space-y-2 text-sm text-slate-700">{list(ai.donts).map((x, i) => <li key={i}>· {x}</li>)}</ul>
              </div>
            </div>
          </Section>
        )}

        {Object.values(d.contact || {}).some(Boolean) && (
          <Section title="Kontakt" accent={accent}>
            <div className="grid sm:grid-cols-2 gap-6">
              {[["email", "E-Mail"], ["phone", "Telefon"], ["website", "Website"], ["social", "Social Media"]].map(([k, label]) =>
                d.contact[k] ? (
                  <div key={k}>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
                    <div className="text-base text-slate-900 mt-1 break-all">{d.contact[k]}</div>
                  </div>
                ) : null,
              )}
            </div>
          </Section>
        )}
      </main>

      <footer className="px-6 sm:px-12 py-10 border-t border-slate-200">
        <div className="max-w-5xl mx-auto flex items-center justify-between text-[11px] text-slate-400">
          <span>by We Academy of Arts and Tech</span>
          <a href="https://www.we-academy.de" target="_blank" rel="noreferrer" className="hover:text-slate-700">www.we-academy.de</a>
        </div>
      </footer>
    </div>
  );
}

const Section = ({ title, accent, children }) => (
  <motion.section initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.5 }}>
    <div className="text-[10px] font-bold uppercase tracking-[0.3em] mb-5" style={{ color: accent }}>{title}</div>
    {children}
  </motion.section>
);
